<script lang="ts">
  import { _ } from "svelte-i18n";
  import { createEventDispatcher, onMount } from "svelte";
  import Size from "../Size/Size.svelte";
  import Typography from "$lib/components/Typography/Typography.svelte";

  export let margin = "";
  export let selectedSizeOption: {
    value?: string;
    inStock?: boolean;
    displayName?: string;
  } = {};
  export let sizeOptions: { value: string; inStock?: boolean }[];
  export let small = false;
  export let url = "";

  export let altSelect = undefined;

  export let waist = false;
  export let leg = false;

  const dispatch = createEventDispatcher();

  const selectSize = (event: CustomEvent) => {
    if (altSelect) {
      altSelect(event);
    } else {
      selectedSizeOption = event.detail;
      dispatch("select", selectedSizeOption);
    }
  };

  // convert size phrases to letter symbols
  const sizeTexts = [
    {
      symbol: "3XS",
      phrase: ["XXXS", "Triple Extra Small", "triple extra small"],
    },
    {
      symbol: "2XS",
      phrase: ["XXS", "Extra Extra Small", "extra extra small"],
    },
    {
      symbol: "XS",
      phrase: ["Extra Small", "extra small"],
    },
    {
      symbol: "S",
      phrase: ["Small", "small"],
    },
    {
      symbol: "M",
      phrase: ["Medium", "medium"],
    },
    {
      symbol: "L",
      phrase: ["Large", "large"],
    },
    {
      symbol: "XL",
      phrase: ["Extra Large", "extra large"],
    },
    {
      symbol: "2XL",
      phrase: [
        "XXL",
        "Double Extra Large",
        "Extra Extra Large",
        "double extra large",
        "extra extra large",
      ],
    },
    {
      symbol: "3XL",
      phrase: ["XXXL", "Triple Extra Large", "triple extra large"],
    },
    {
      symbol: "4XL",
      phrase: ["XXXXL", "Quadruple Extra Large", "quadruple extra large"],
    },
    {
      symbol: "5XL",
      phrase: ["XXXXXL", "Quintuple Extra Large", "quintuple extra large"],
    },
    {
      symbol: "6XL",
      phrase: ["XXXXXXL", "Sextuple Extra Large", "Sextuple extra large"],
    },
  ];

  const sortOrder = sizeTexts.map(st => st.symbol);
  const sortOrderObj = sortOrder.reduce((acc, cur, i) => {
    acc[cur] = i;
    return acc;
  }, {});

  let displaySize = "";
  let sizeOptionsDisplay: {
    value: string;
    inStock?: boolean;
    displayName: string;
  }[] = [];

  $: {
    sizeOptionsDisplay =
      sizeOptions &&
      sizeOptions.map(sizeOption => {
        displaySize = sizeOption.value;
        sizeTexts.forEach(sizeText => {
          if (sizeText.phrase.includes(displaySize)) {
            displaySize = sizeText.symbol;
          }
        });
        return {
          value: sizeOption.value,
          inStock: sizeOption.inStock,
          displayName: displaySize,
        };
      });

    sizeOptionsDisplay &&
      sizeOptionsDisplay
        .sort((a, b) => parseFloat(a.displayName) - parseFloat(b.displayName))
        .sort(
          (a, b) => sortOrderObj[a.displayName] - sortOrderObj[b.displayName],
        );
  }

  let viewportWidth: number;
  let isMobile = false;
  let lgWidth = 1280;

  onMount(() => {
    const bodyStyle = getComputedStyle(document.body);
    lgWidth = Number(bodyStyle.getPropertyValue("--lg").replace(/\D/g, ""));
  });

  $: viewportWidth >= lgWidth ? (isMobile = false) : (isMobile = true);
</script>

<style lang="scss">
  .size-swatch {
    @include breakpoint(lg) {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    &--hover-message {
      padding-bottom: 8px;
    }
    &--sizes-container {
      overflow: hidden;
      position: relative;
    }
    &--sizes-wrapper {
      line-height: 0;
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }
  }
  .size-guide-link {
    margin: 12px 0 0;
    width: fit-content;
    align-self: flex-start;
    @include breakpoint(lg) {
      margin: 6px 0 0;
    }
  }
  .inline-anchor:hover {
    color: $on-surface-30;
    cursor: pointer;
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

{#if sizeOptions}
  <div class="size-swatch">
    <div class="size-swatch--sizes-container">
      {#if small && !(waist || leg)}
        <div class="size-swatch--hover-message">
          <Typography component="span" variant="body1" theme="on-surface-30"
            >{$_("Sizes available")}:</Typography
          >
        </div>
      {/if}

      <div class="size-swatch--sizes-wrapper">
        {#each sizeOptionsDisplay as sizeOption}
          <Size
            href={url && `${url}?size=${sizeOption.value}`}
            {margin}
            {small}
            {sizeOption}
            selected={selectedSizeOption.value === sizeOption.value}
            on:select={e => {
              selectSize(e);
            }}
            preventDefault={altSelect !== undefined ? true : false}
          />
        {/each}
      </div>
    </div>
  </div>
{/if}
